// HooksPanelV3 — multi-webhook management for a source.
//
// Replaces the single-webhook URL display with a list of named hooks,
// each with its own URL, key/value tags, and ⋯ menu (Rename, Edit
// tags, Archive). Operator can have many hooks per source — Stripe
// production, Stripe staging, Slack, GitHub — each tagged so views
// can filter by hook attribution.
//
// Hooks are surfaced as `SourceInput` records with kind='webhook';
// existing input-PATCH semantics handle name/status/tags. Per-hook
// URL is `/hook/<source_id>/<input_id>` (the bare /hook/<source_id>
// path still works for backward-compat — it lands on the FIRST
// webhook input).

import React, { useState } from 'react';
import type { Source, SourceInput } from '@datahub/shared';
import { createSourceWebhook, patchSourceInput } from '../../api';
import { useToast } from '../Toast';
import { TagChip } from '../../lib/tag-colors';

export function HooksPanelV3({ source, onChange }: {
  source: Source;
  onChange: () => void;
}): React.ReactElement {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showTagsDialog, setShowTagsDialog] = useState<string | null>(null);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState('');

  // Render every non-archived input — webhook + file_upload +
  // api_pull + email + manual. UI is identical (name + tag chips +
  // url/address row + ⋯ menu); per-kind nuances surface via the
  // 'kind' chip and the URL-vs-address copy. Operator can have many
  // of any kind on a source.
  const hooks = source.inputs
    .filter(i => i.status !== 'archived')
    .sort((a, b) => a.input_id.localeCompare(b.input_id));

  function copy(text: string, label: string): void {
    void navigator.clipboard.writeText(text);
    toast.show(`${label} copied`, { kind: 'success' });
  }

  async function rename(hook: SourceInput, newName: string): Promise<void> {
    setBusy(hook.input_id);
    try {
      await patchSourceInput(source.source_id, hook.input_id, { name: newName.trim() || null });
      toast.show('Hook renamed', { kind: 'success' });
      setEditingNameId(null);
      onChange();
    } catch (e) {
      toast.show(`Rename failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setBusy(null);
    }
  }

  async function archive(hook: SourceInput): Promise<void> {
    const ok = await toast.confirm({
      title: 'Archive this hook?',
      message: `${hook.name ?? hook.input_id} will stop accepting events. URL returns 410. You can restore later via PATCH status='active'.`,
      destructive: true,
      confirmLabel: 'Archive hook',
    });
    if (!ok) return;
    setBusy(hook.input_id);
    try {
      await patchSourceInput(source.source_id, hook.input_id, { status: 'archived' });
      toast.show('Hook archived', { kind: 'success' });
      onChange();
    } catch (e) {
      toast.show(`Archive failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {hooks.map(hook => {
        const isFirst = hook === hooks[0];
        const KIND_EMOJI: Record<string, string> = {
          webhook: '🌐', file_upload: '📁', api_pull: '🔄', email: '📧', manual: '🧪',
        };
        const KIND_LABEL: Record<string, string> = {
          webhook: 'Webhook', file_upload: 'File upload', api_pull: 'API pull', email: 'Email', manual: 'Manual',
        };
        const target = hook.kind === 'email' ? hook.address : hook.url;
        const url = target ?? `${hook.kind}://${source.source_id}/${hook.input_id}`;
        const tagEntries = hook.tags ? Object.entries(hook.tags) : [];
        return (
          <div
            key={hook.input_id}
            style={{
              padding: '10px 12px',
              background: 'var(--bg)',
              border: '1px solid var(--line)',
              borderRadius: 6,
              display: 'flex', flexDirection: 'column', gap: 6,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {editingNameId === hook.input_id ? (
                <input
                  autoFocus
                  value={nameDraft}
                  onChange={e => setNameDraft(e.target.value)}
                  onBlur={() => { void rename(hook, nameDraft); }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { void rename(hook, nameDraft); }
                    if (e.key === 'Escape') { setEditingNameId(null); }
                  }}
                  style={{
                    fontFamily: 'var(--ui)', fontSize: 13, fontWeight: 500,
                    padding: '2px 6px', minWidth: 180,
                    border: '1px solid var(--accent-line)', borderRadius: 3,
                    background: 'var(--bg-elev)', color: 'var(--ink)',
                  }}
                />
              ) : (
                <strong
                  onDoubleClick={() => { setNameDraft(hook.name ?? ''); setEditingNameId(hook.input_id); }}
                  title="Double-click to rename"
                  style={{ fontFamily: 'var(--ui)', fontSize: 13, fontWeight: 500, color: 'var(--ink)', cursor: 'text' }}
                >
                  {hook.name ?? (isFirst ? `Default ${KIND_LABEL[hook.kind] ?? hook.kind}` : `${KIND_LABEL[hook.kind] ?? hook.kind} ${hook.input_id.slice(-6)}`)}
                </strong>
              )}
              <span
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  padding: '1px 6px', fontSize: 10, fontFamily: 'var(--mono)',
                  background: 'var(--bg-sunken)', border: '1px solid var(--line)',
                  borderRadius: 8, color: 'var(--ink-3)',
                }}
                title={KIND_LABEL[hook.kind] ?? hook.kind}
              ><span aria-hidden="true">{KIND_EMOJI[hook.kind] ?? '·'}</span> {hook.kind}</span>
              {tagEntries.length > 0 && (
                <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {tagEntries.map(([k, v]) => (
                    <TagChip key={k} k={k} v={v} compact />
                  ))}
                </span>
              )}
              <span style={{ flex: 1 }} />
              <button
                type="button"
                onClick={() => {
                  // KPI #4: 1-click curl. Operator pastes this directly
                  // into a terminal and tests the hook end-to-end.
                  // Sample payload chosen for shape clarity, not realism.
                  const curl = [
                    `curl -X POST '${url}' \\`,
                    `  -H 'Content-Type: application/json' \\`,
                    `  -d '{"event":"hello","value":42}'`,
                  ].join('\n');
                  void navigator.clipboard.writeText(curl);
                  toast.show(`curl for "${hook.name ?? hook.input_id.slice(-6)}" copied`, { kind: 'success' });
                }}
                className="btn sm ghost"
                style={{ padding: '2px 8px', fontSize: 11, fontFamily: 'var(--mono)' }}
                title="Copy a curl command for this specific hook — paste into a terminal to test ingestion"
              >curl</button>
              <button
                type="button"
                onClick={() => setShowTagsDialog(hook.input_id)}
                className="btn sm ghost"
                style={{ padding: '2px 8px', fontSize: 11 }}
                title="Edit key/value tags for filtering"
              >tags</button>
              <button
                type="button"
                onClick={() => { void archive(hook); }}
                disabled={busy === hook.input_id}
                className="btn sm ghost"
                style={{ padding: '2px 8px', fontSize: 11, color: 'var(--danger)' }}
                title="Archive this hook"
                aria-label={`Archive ${hook.kind} ${hook.name ?? hook.input_id.slice(-6)}`}
              ><span aria-hidden="true">×</span></button>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <input
                readOnly
                value={url}
                onClick={e => (e.target as HTMLInputElement).select()}
                style={{
                  flex: 1, padding: '6px 10px',
                  fontFamily: 'var(--mono)', fontSize: 11.5,
                  border: '1px solid var(--line)', borderRadius: 4,
                  background: 'var(--bg-sunken)', color: 'var(--ink)',
                }}
              />
              <button
                type="button"
                onClick={() => copy(url, 'URL')}
                className="btn sm ghost"
                style={{ padding: '2px 10px', fontSize: 11 }}
              >copy</button>
            </div>
          </div>
        );
      })}
      <button
        type="button"
        onClick={() => setShowAddDialog(true)}
        className="btn sm ghost"
        style={{
          padding: '6px 12px', fontSize: 12,
          alignSelf: 'flex-start',
          border: '1px dashed var(--line)',
        }}
      >+ Add input</button>

      {showAddDialog && (
        <AddHookDialog
          sourceId={source.source_id}
          onClose={() => setShowAddDialog(false)}
          onCreated={() => { setShowAddDialog(false); onChange(); }}
        />
      )}

      {showTagsDialog && (
        <EditTagsDialog
          source={source}
          inputId={showTagsDialog}
          onClose={() => setShowTagsDialog(null)}
          onSaved={() => { setShowTagsDialog(null); onChange(); }}
        />
      )}
    </div>
  );
}

/** Multi-kind add-input dialog. Kind picker first, then per-kind
 *  fields (URL for api_pull, address override for email). */
function AddHookDialog({ sourceId, onClose, onCreated }: {
  sourceId: string;
  onClose: () => void;
  onCreated: () => void;
}): React.ReactElement {
  const toast = useToast();
  const [kind, setKind] = useState<'webhook' | 'file_upload' | 'api_pull' | 'email' | 'manual'>('webhook');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [tagRows, setTagRows] = useState<Array<{ key: string; value: string }>>([{ key: '', value: '' }]);
  const [submitting, setSubmitting] = useState(false);

  async function submit(): Promise<void> {
    setSubmitting(true);
    const tags: Record<string, string> = {};
    for (const r of tagRows) {
      if (r.key.trim()) tags[r.key.trim()] = r.value;
    }
    try {
      if (kind === 'webhook') {
        await createSourceWebhook(sourceId, {
          name: name.trim() || undefined,
          tags: Object.keys(tags).length > 0 ? tags : undefined,
        });
      } else {
        const res = await fetch(`/api/v1/sources/${sourceId}/inputs/${kind}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...(name.trim() ? { name: name.trim() } : {}),
            ...(Object.keys(tags).length > 0 ? { tags } : {}),
            ...(kind === 'api_pull' && url ? { url } : {}),
          }),
        });
        if (!res.ok) {
          const e = await res.json().catch(() => ({})) as { error?: { detail?: string } };
          throw new Error(e.error?.detail ?? `HTTP ${res.status}`);
        }
      }
      toast.show(`${kind === 'webhook' ? 'Hook' : 'Input'} created`, { kind: 'success' });
      onCreated();
    } catch (e) {
      toast.show(`Create failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  const KIND_OPTIONS = [
    { id: 'webhook', label: '🌐 Webhook' },
    { id: 'file_upload', label: '📁 File upload' },
    { id: 'api_pull', label: '🔄 API pull' },
    { id: 'email', label: '📧 Email' },
    { id: 'manual', label: '🧪 Manual' },
  ] as const;

  return (
    <ModalShell title="Add an input" onClose={onClose}>
      <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Kind</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
        {KIND_OPTIONS.map(opt => {
          const active = kind === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setKind(opt.id)}
              style={{
                padding: '4px 10px', fontSize: 11.5,
                background: active ? 'var(--accent)' : 'var(--bg)',
                color: active ? 'white' : 'var(--ink-2)',
                border: `1px solid ${active ? 'var(--accent)' : 'var(--line)'}`,
                borderRadius: 14, cursor: 'pointer',
                fontWeight: active ? 500 : 400,
              }}
            >{opt.label}</button>
          );
        })}
      </div>
      <input
        autoFocus
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder={`Input name (e.g. ${kind === 'webhook' ? "'Stripe production'" : kind === 'file_upload' ? "'EOD CSV drop'" : kind === 'api_pull' ? "'Hourly sync'" : kind === 'email' ? "'Order intake'" : "'Manual entry'"})`}
        style={modalInputStyle}
      />
      {kind === 'api_pull' && (
        <input
          value={url}
          onChange={e => setUrl(e.target.value)}
          placeholder="Upstream URL (https://api.example.com/orders)"
          style={{ ...modalInputStyle, marginTop: 8 }}
        />
      )}
      <div style={{ marginTop: 12, fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Tags</div>
      {tagRows.map((row, idx) => (
        <div key={idx} style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <input
            value={row.key}
            onChange={e => setTagRows(prev => prev.map((r, i) => i === idx ? { ...r, key: e.target.value } : r))}
            placeholder="key (e.g. env)"
            style={{ ...modalInputStyle, flex: 1 }}
          />
          <input
            value={row.value}
            onChange={e => setTagRows(prev => prev.map((r, i) => i === idx ? { ...r, value: e.target.value } : r))}
            placeholder="value (e.g. prod)"
            style={{ ...modalInputStyle, flex: 1 }}
          />
          <button
            type="button"
            onClick={() => setTagRows(prev => prev.filter((_, i) => i !== idx))}
            style={{ background: 'transparent', border: '1px solid var(--line)', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', color: 'var(--ink-3)' }}
          >×</button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => setTagRows(prev => [...prev, { key: '', value: '' }])}
        className="btn sm ghost"
        style={{ marginTop: 6, padding: '4px 10px', fontSize: 11, alignSelf: 'flex-start' }}
      >+ tag</button>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" onClick={onClose} className="btn sm ghost" disabled={submitting}>Cancel</button>
        <button
          type="button"
          onClick={() => { void submit(); }}
          className="btn sm primary"
          disabled={submitting}
        >{submitting ? 'Creating…' : 'Create hook'}</button>
      </div>
    </ModalShell>
  );
}

/** Edit tags on an existing hook. Reuses the AddHookDialog tag editor
 *  shape — full replacement on save. */
function EditTagsDialog({ source, inputId, onClose, onSaved }: {
  source: Source;
  inputId: string;
  onClose: () => void;
  onSaved: () => void;
}): React.ReactElement {
  const toast = useToast();
  const hook = source.inputs.find(i => i.input_id === inputId);
  const initial = hook?.tags ? Object.entries(hook.tags) : [];
  const [tagRows, setTagRows] = useState<Array<{ key: string; value: string }>>(
    initial.length > 0 ? initial.map(([k, v]) => ({ key: k, value: v })) : [{ key: '', value: '' }],
  );
  const [submitting, setSubmitting] = useState(false);

  async function submit(): Promise<void> {
    if (!hook) return;
    setSubmitting(true);
    const tags: Record<string, string> = {};
    for (const r of tagRows) {
      if (r.key.trim()) tags[r.key.trim()] = r.value;
    }
    try {
      await patchSourceInput(source.source_id, inputId, {
        tags: Object.keys(tags).length > 0 ? tags : null,
      });
      toast.show('Tags updated', { kind: 'success' });
      onSaved();
    } catch (e) {
      toast.show(`Save failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title={`Tags · ${hook?.name ?? inputId.slice(-6)}`} onClose={onClose}>
      <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 8 }}>
        Tags get stamped on every event from this hook as <code style={{ fontFamily: 'var(--mono)' }}>origin.hook_tags.&lt;key&gt;</code> — use them in view filters.
      </div>
      {tagRows.map((row, idx) => (
        <div key={idx} style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <input
            value={row.key}
            onChange={e => setTagRows(prev => prev.map((r, i) => i === idx ? { ...r, key: e.target.value } : r))}
            placeholder="key"
            style={{ ...modalInputStyle, flex: 1 }}
          />
          <input
            value={row.value}
            onChange={e => setTagRows(prev => prev.map((r, i) => i === idx ? { ...r, value: e.target.value } : r))}
            placeholder="value"
            style={{ ...modalInputStyle, flex: 1 }}
          />
          <button
            type="button"
            onClick={() => setTagRows(prev => prev.filter((_, i) => i !== idx))}
            style={{ background: 'transparent', border: '1px solid var(--line)', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', color: 'var(--ink-3)' }}
          >×</button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => setTagRows(prev => [...prev, { key: '', value: '' }])}
        className="btn sm ghost"
        style={{ marginTop: 6, padding: '4px 10px', fontSize: 11, alignSelf: 'flex-start' }}
      >+ tag</button>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" onClick={onClose} className="btn sm ghost" disabled={submitting}>Cancel</button>
        <button
          type="button"
          onClick={() => { void submit(); }}
          className="btn sm primary"
          disabled={submitting}
        >{submitting ? 'Saving…' : 'Save tags'}</button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, children, onClose }: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}): React.ReactElement {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'oklch(0.18 0.01 250 / 0.40)',
        zIndex: 1500, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 8,
          padding: '20px 24px', minWidth: 380, maxWidth: 480,
          boxShadow: '0 12px 40px oklch(0.18 0.01 250 / 0.30)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
          <strong style={{ fontSize: 16 }}>{title}</strong>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'transparent', border: 'none', color: 'var(--ink-3)', cursor: 'pointer', fontSize: 16, padding: 0 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const modalInputStyle: React.CSSProperties = {
  padding: '8px 10px', fontFamily: 'var(--mono)', fontSize: 13,
  border: '1px solid var(--line)', borderRadius: 4,
  background: 'var(--bg)', color: 'var(--ink)',
};
